import Vue from 'vue'
import Vuex from 'vuex'
import createPersistedState from 'vuex-persistedstate';
import DefaultPicture from '@/assets/default-picture.svg'
import brokerModule from '@/apps/broker/store'
import ibModule from '@/apps/ib/store'

Vue.use(Vuex);

export default new Vuex.Store({
    plugins: [createPersistedState({
        // Exclude transient state that should not persist across page refreshes
        // This prevents stale tournament data from loading on refresh
        reducer: (state) => {
            const { tournament, ...persistedState } = state;
            return persistedState;
        }
    })],
    modules: {
        broker: brokerModule,
        ib: ibModule
    },
    state: {
        backend: process.env.VUE_APP_BACKEND_URL,

        token: '',
        preferredLanguage: '',
        analyticsPath: 'https://us-central1-swiset-admin.cloudfunctions.net/app',
        paypalSubscriptionsScript: 'https://www.paypal.com/sdk/js?client-id=ATafAIbtL5aO_30PailV-bTi3xhijOWQJkJEvU4_5YQeyl7Tbr4Ua-djoTVe8FJ-7ydl7imoR9MC950u&vault=true',
        user: {
            id: 0,
            notificationDisplayed: false,
            isPro: false,
            username: '',
            firstName: '',
            lastName: '',
            email: '',
            imageUrl: DefaultPicture,
            assets: [],
            brokerAccounts: [],
            strategies: [],
            considerations: [],
            details: {
                id: 0,
                convertedFrom: '',
                phoneNumber: '',
                country: '',
                type: '',
                profileDescription: '',
                automationToken: '',
                profileStatus: '',
                telegramId: '',
                referredFrom: '',
                usageState: '',
                commercialState: '',
                channel: '',
            },
        },
        extUser: {
            id: 0,
            assets: [],
            brokerAccounts: [],
            strategies: [],
        },
        book: {
            noTrades: false,
            loadingTrades: true,
            currentFilter: {
                fromDate: null,
                toDate: null,
                side: null,
                asset: null,
                mood: null,
                brokerAccount: null,
            },
            currentFilterExtUser: {
                fromDate: null,
                toDate: null,
                side: null,
                asset: null,
                mood: null,
                brokerAccount: null,
                userId: null,
            },
            stats: {
                tradeTimeAvg: '',
            },
            trades: [],
            totalElements: 0,
            totalPages: 0,
            pageable: {
                size: 15,
                page: 0,
                sort: 'openTime,desc',
            },
            filterMade: false,
            moods: [
                'Anxious',
                'Happy',
                'Relaxed',
                'Cautious',
                'Concentrated',
                'Confident',
                'Excited',
                'Happy',
                'Optimistic',
                'Aggressive',
                'Angry',
                'Bored',
                'Hurt',
                'Mad',
                'Nervous',
                'Sad',
                'Upset',
                'Sick',
                'None',
            ],
        },
        shared: {
            id: '',
            username: '',
            imageUrl: '',
            firstName: '',
            lastName: '',
            country: '',
            allowComments: false,
        },
        tournament: null,
        fundingTest: null,

        // Club Related Variables
        clubMember: null,
        clubMembers: [],
        clubEvents: [],
        clubPosts: [],
        clubChannels: [],
        clubPublicChannels: [],
        currentClubChannel: { id: -1, name: 'GENERAL' },
        clubChannelsContributedByMe: [],
        clubTournaments: [],
        clubPermissions: [],

        sides: ['Buy', 'Sell'],
        results: ['Winning', 'Losing'],
        markets: [],
        ads: [],

        whitelabel: {
            userEmail: null,
            userId: null,
            partner: null,
            apiKey: null,
            features: [],
            isNewUser: false,
            context: null,
            platform: null,
            isInvestorPasswordRequired: true
        },
        appThemes: {},
        currentTheme: {},
        currentDetailedInfo: undefined,

        tournamentForm: {
            basicInfo: {
                name: '',
                startDate: '',
                endDate: '',
                faqsLink: '',
                termsCondition: "",
                selectedClubId: null // For broker app: selected community to create tournament in
            },
            details: {
                visibility: 'PRIVATE',
                tournamentType: '', // New field for DEMO/LIVE
                platform: '', // Platform name (e.g., MT5ManagerAPI)
                platformGroup: '', // Platform group (e.g., demo\\USD*)
                platformGroupId: null, // Platform group ID for API calls
                broker: 'ALL', // Legacy field - kept for compatibility
                selfManaged: false,
                publicPerformance: 0
            },
            participants: {
                // accountType removed - now derived from details.tournamentType
                rankingType: '', // Empty by default - will be set from available options
                registrationPeriod: 'NOT_ALLOWED',
                inviteOnly: false
            },
            rules: [],
            initialBalance: '', // Add this new field
            awards: []  // Add this new property
        },
        // Store platform info for other wizard steps
        tournamentPlatformInfo: {
            platform: '',
            tournamentType: '',
            platformGroup: '',
            platformGroupId: null
        }
    },
    mutations: {
        setToken(state, token) {
            state.token = token;
        },
        setPreferredLanguage(state, language) {
            state.preferredLanguage = language;
        },
        setIsPro(state, isPro) {
            state.user.isPro = isPro;
        },
        setNotificationDisplayed(state, nDisplayed) {
            state.user.notificationDisplayed = nDisplayed;
        },
        setUser(state, user) {
            state.user.id = user.id;
            state.user.username = user.login;
            state.user.firstName = user.firstName;
            state.user.lastName = user.lastName;
            state.user.email = user.email;
            if (user.imageUrl != null && user.imageUrl !== '')
                state.user.imageUrl = user.imageUrl;
        },
        setUserDetails(state, userDetails) {
            state.user.details.id = userDetails.id;
            state.user.details.profileDescription = userDetails.profileDescription;
            state.user.details.type = userDetails.type;
            state.user.details.phoneNumber = userDetails.phoneNumber;
            state.user.details.country = userDetails.country;
            state.user.details.convertedFrom = userDetails.convertedFrom;
            state.user.details.automationToken = userDetails.automationToken;
            state.user.details.profileStatus = userDetails.profileStatus;
            state.user.details.telegramId = userDetails.telegramId;
            state.user.details.referredFrom = userDetails.referredFrom;
            state.user.details.usageState = userDetails.usageState;
            state.user.details.commercialState = userDetails.commercialState;
            state.user.details.channel = userDetails.arrivedFromChannel;
        },
        setCompletedRegister(state, completeRegister) {
            state.user.firstName = completeRegister.firstName;
            state.user.lastName = completeRegister.lastName;
            state.user.details.phoneNumber = completeRegister.phoneNumber;
            state.user.details.country = completeRegister.country;
            state.user.details.type = completeRegister.typeOfTrader;
        },
        setNewProfile(state, profile) {
            let oldUsername = state.user.username;
            let newUsername = profile.username;
            let oldAutomationToken = state.user.details.automationToken;

            state.user.firstName = profile.firstName;
            state.user.lastName = profile.lastName;
            state.user.details.phoneNumber = profile.phoneNumber;
            state.user.details.profileDescription = profile.profileDescription;
            state.user.username = newUsername;
            state.user.details.automationToken = oldAutomationToken.replace(oldUsername, newUsername);
            state.user.details.referredFrom = profile.referredFrom;
        },
        setNewVisibility(state, visibility) {
            state.user.details.profileStatus = visibility;
        },
        setTelegramId(state, telegramId) {
            state.user.details.telegramId = telegramId;
        },
        setMarkets(state, markets) {
            state.markets = markets;
        },
        setFilterMade(state, filterMade) {
            state.book.filterMade = filterMade;
        },
        setAssets(state, assets) {
            state.user.assets = assets;
        },
        setStrategies(state, strategies) {
            state.user.strategies = strategies;
        },
        setConsiderations(state, considerations) {
            state.user.considerations = considerations;
        },
        setBrokerAccounts(state, accounts) {
            state.user.brokerAccounts = accounts;
        },
        setExtUser(state, user) {
            state.extUser.id = user.id;
            state.extUser.assets = user.assets;
            state.extUser.strategies = user.strategies;
            state.extUser.brokerAccounts = user.brokerAccounts;
        },
        addStrategy(state, strategy) {
            state.user.strategies.push(strategy);
        },
        setTournament(state, tournament) {
            state.tournament = tournament;
        },
        resetTournament(state) {
            state.tournament = null;
        },
        setFundingTest(state, fundingTest) {
            state.fundingTest = fundingTest
        },
        setClubMember(state, clubMember) {
            state.clubMember = clubMember;
        },
        setClubMembers(state, members) {
            state.clubMembers = members;
        },
        setClubEvents(state, events) {
            state.clubEvents = events;
        },
        setClubPosts(state, posts) {
            state.clubPosts = posts;
        },
        setClubChannels(state, clubChannels) {
            state.clubChannels = clubChannels;
        },
        setClubPublicChannels(state, clubPublicChannels) {
            state.clubPublicChannels = clubPublicChannels;
        },
        resetCurrentClubChannel(state) {
            state.currentClubChannel = { id: -1, name: 'GENERAL' };
        },
        setCurrentClubChannel(state, changedClubChannel) {
            state.currentClubChannel = changedClubChannel;
        },
        setClubChannelsContributedByMe(state, clubChannelContributions) {
            state.clubChannelsContributedByMe = clubChannelContributions;
        },
        setClubTournaments(state, tournaments) {
            state.clubTournaments = tournaments;
        },
        setClubPermissions(state, permissions) {
            state.clubPermissions = permissions;
        },
        setShare(state, share) {
            state.shared = share;
        },
        setAutomationToken(state, automationToken) {
            state.user.details.automationToken = automationToken;
        },
        unpairTelegram(state) {
            state.user.details.telegramId = '';
        },
        //Trading Journal
        setNoTrades(state, noTrades) {
            state.book.noTrades = noTrades;
        },
        setTrades(state, trades) {
            state.book.trades = trades;
            state.book.loadingTrades = false;
        },
        setTradesPageable(state, pageable) {
            state.book.pageable = pageable;
            state.book.loadingTrades = true;
        },
        setTradesPageInfo(state, pageInfo) {
            state.book.totalElements = pageInfo.totalElements;
            state.book.totalPages = pageInfo.totalPages;
        },
        setLoadingTrades(state, loading) {
            state.book.loadingTrades = loading;
        },
        setStats(state, stats) {
            state.book.stats = stats;
        },
        setCurrentFilter(state, currentFilter) {
            state.book.currentFilter = currentFilter;
        },
        setCurrentFilterExtUser(state, currentFilter) {
            state.book.currentFilterExtUser = currentFilter;
        },
        setAds(state, ads) {
            state.ads = ads;
        },
        setWhitelabelUtils(state, utils) {
            state.whitelabel.userEmail = utils.userEmail;
            state.whitelabel.userId = utils.userId;
            state.whitelabel.partner = utils.partner;
            state.whitelabel.apiKey = utils.embedKey;
            state.whitelabel.features = utils.features;
            state.whitelabel.context = utils.context;
            state.whitelabel.platform = utils.platform;
            state.whitelabel.isInvestorPasswordRequired = utils.isInvestorPasswordRequired
        },
        resetWhitelabel(state) {
            state.whitelabel = {
                userEmail: null,
                userId: null,
                partner: null,
                apiKey: null,
                features: [],
                isNewUser: false,
                context: null,
                platform: null,
                isInvestorPasswordRequired: true
            }
        },
        clearData(state) {
            state.token = '';
            state.book.trades = [];
            state.book.noTrades = false;
            state.book.loadingTrades = true;
            state.ads = [];
            state.tournament = null;
            state.fundingTest = null;
            state.user = {
                id: 0,
                notificationDisplayed: false,
                isPro: false,
                username: '',
                firstName: '',
                lastName: '',
                email: '',
                imageUrl: DefaultPicture,
                assets: [],
                brokerAccounts: [],
                strategies: [],
                considerations: [],
                details: {
                    id: 0,
                    convertedFrom: '',
                    phoneNumber: '',
                    country: '',
                    type: '',
                    profileDescription: '',
                    automationToken: '',
                    profileStatus: '',
                },
            };
            state.extUser = {
                id: 0,
                assets: [],
                brokerAccounts: [],
                strategies: [],
            };
            state.whitelabel = {
                userEmail: null,
                userId: null,
                partner: null,
                apiKey: null,
                features: [],
                isNewUser: false,
                context: null
            };
        },
        setAppThemes(state, themes) {
            state.appThemes = themes
        },
        setCurrentTheme(state, theme) {
            state.currentTheme = theme
        },
        updateDetailedInfo(state, participantDetailedInfo) {
            state.currentDetailedInfo = participantDetailedInfo;
        },
        updateTournamentBasicInfo(state, basicInfo) {
            state.tournamentForm.basicInfo = { ...basicInfo };
        },
        updateTournamentDetails(state, details) {
            state.tournamentForm.details = { ...details };
        },
        updateTournamentParticipants(state, participants) {
            state.tournamentForm.participants = { ...participants };
        },
        updateTournamentRules(state, rules) {
            state.tournamentForm.rules = [...rules];
        },
        updateTournamentInitialBalance(state, value) {
            state.tournamentForm.initialBalance = value;
        },
        updateTournamentAwards(state, awards) {
            state.tournamentForm.awards = [...awards];
        },
        updateTournamentPlatformInfo(state, platformInfo) {
            state.tournamentPlatformInfo = { ...platformInfo };
        },
        resetTournamentForm(state) {
            state.tournamentForm = {
                basicInfo: {
                    name: '',
                    startDate: '',
                    endDate: '',
                    startTime: '09:00',
                    endTime: '18:00',
                    faqsLink: '',
                    termsCondition: "",
                    mainLanguage: 'en',
                    selectedClubId: null // For broker app: selected community to create tournament in
                },
                details: {
                    visibility: 'PRIVATE',
                    tournamentType: '', // New field for DEMO/LIVE
                    platform: '', // Platform name
                    platformGroup: '', // Platform group
                    platformGroupId: null, // Platform group ID
                    broker: 'ALL', // Legacy field
                    selfManaged: false,
                    publicPerformance: 0
                },
                participants: {
                    // accountType removed - now derived from details.tournamentType
                    rankingType: '', // Empty by default - will be set from available options
                    registrationPeriod: 'NOT_ALLOWED',
                    inviteOnly: false
                },
                rules: [],
                initialBalance: '', // Add this to reset
                awards: []  // Add this to reset
            };
        }
    },
    actions: {
        updateAppThemes({ commit }, themes) {
            commit("setAppThemes", themes)
        },
        setClubMembers({ commit }, members) {
            commit("setClubMembers", members)
        }
    }
})
